import type { LetterGrade } from "./scoring";
import { createRng, hashSeed } from "./rng";

export type DailyOpKind =
  | "kills"
  | "elites"
  | "extract"
  | "chain"
  | "salvage"
  | "survive"
  | "ace"
  | "grade";

export type DailyOp = {
  id: string;
  kind: DailyOpKind;
  title: string;
  description: string;
  target: number;
  progress: number;
  claimed: boolean;
  salvageReward: number;
  rpReward: number;
};

export type DailyOpsState = {
  dateKey: string;
  ops: DailyOp[];
};

export type DailyOpEvent =
  | { type: "kill"; elite: boolean; ace: boolean }
  | { type: "salvage"; amount: number }
  | { type: "chain"; count: number }
  | { type: "outcome"; extracted: boolean; grade: LetterGrade; simTime: number };

const GRADE_RANK: Record<LetterGrade, number> = { D: 1, C: 2, B: 3, A: 4, S: 5 };

type CatalogEntry = {
  kind: DailyOpKind;
  title: string;
  describe: (target: number) => string;
  targets: readonly number[];
  salvage: number;
  rp: number;
};

const CATALOG: readonly CatalogEntry[] = [
  {
    kind: "kills",
    title: "Sweep",
    describe: (n) => `Destroy ${n} hostiles`,
    targets: [18, 28, 40],
    salvage: 18,
    rp: 8,
  },
  {
    kind: "elites",
    title: "Core Hunt",
    describe: (n) => `Destroy ${n} elites`,
    targets: [2, 3, 4],
    salvage: 24,
    rp: 10,
  },
  {
    kind: "extract",
    title: "Home Runs",
    describe: (n) => `Extract ${n} time${n === 1 ? "" : "s"}`,
    targets: [1, 2],
    salvage: 22,
    rp: 12,
  },
  {
    kind: "chain",
    title: "Unbroken",
    describe: (n) => `Reach a ${n}-hit combo`,
    targets: [8, 12, 16],
    salvage: 16,
    rp: 8,
  },
  {
    kind: "salvage",
    title: "Scrapper",
    describe: (n) => `Collect ${n} salvage`,
    targets: [40, 70, 110],
    salvage: 14,
    rp: 6,
  },
  {
    kind: "survive",
    title: "Endurance",
    describe: (n) => `Stay aloft ${n}s`,
    targets: [90, 150, 210],
    salvage: 16,
    rp: 8,
  },
  {
    kind: "ace",
    title: "Ace Hunt",
    describe: () => "Down an Ace",
    targets: [1],
    salvage: 30,
    rp: 14,
  },
  {
    kind: "grade",
    title: "Clean Sortie",
    describe: (n) => `Extract at grade ${gradeFromRank(n)}+`,
    targets: [3, 4],
    salvage: 26,
    rp: 12,
  },
];

export function utcDateKey(nowMs = Date.now()): string {
  return new Date(nowMs).toISOString().slice(0, 10);
}

export function emptyDailyOps(dateKey = utcDateKey()): DailyOpsState {
  return { dateKey, ops: [] };
}

export function createDailyOps(dateKey: string): DailyOpsState {
  const seed = hashDateKey(dateKey);
  const rng = createRng(seed);
  const pool = [...CATALOG];
  const ops: DailyOp[] = [];
  for (let i = 0; i < 3 && pool.length > 0; i += 1) {
    const index = Math.floor(rng() * pool.length);
    const spec = pool.splice(index, 1)[0]!;
    const target = spec.targets[Math.floor(rng() * spec.targets.length)] ?? spec.targets[0]!;
    ops.push({
      id: `${dateKey}:${spec.kind}:${i}`,
      kind: spec.kind,
      title: spec.title,
      description: spec.describe(target),
      target,
      progress: 0,
      claimed: false,
      salvageReward: spec.salvage + target,
      rpReward: spec.rp,
    });
  }
  return { dateKey, ops };
}

export function ensureDailyOps(
  state: DailyOpsState | undefined,
  dateKey: string,
): DailyOpsState {
  if (state && state.dateKey === dateKey && state.ops.length === 3) return state;
  return createDailyOps(dateKey);
}

export function cloneDailyOps(state: DailyOpsState): DailyOpsState {
  return {
    dateKey: state.dateKey,
    ops: state.ops.map((op) => ({ ...op })),
  };
}

/** Apply a run event. Returns ops that just crossed their target (not yet marked claimed). */
export function applyDailyOpEvent(state: DailyOpsState, event: DailyOpEvent): DailyOp[] {
  const newly: DailyOp[] = [];
  for (const op of state.ops) {
    if (op.claimed) continue;
    const before = op.progress;
    op.progress = Math.min(op.target, nextProgress(op, event));
    if (before < op.target && op.progress >= op.target) newly.push(op);
  }
  return newly;
}

export function claimDailyOp(op: DailyOp): boolean {
  if (op.claimed || op.progress < op.target) return false;
  op.claimed = true;
  return true;
}

export function dailyOpsCompleted(state: DailyOpsState): number {
  return state.ops.filter((op) => op.claimed || op.progress >= op.target).length;
}

export function dailyOpsShortLabel(op: DailyOp): string {
  return `${op.title} ${Math.min(op.progress, op.target)}/${op.target}`;
}

function nextProgress(op: DailyOp, event: DailyOpEvent): number {
  switch (op.kind) {
    case "kills":
      return event.type === "kill" ? op.progress + 1 : op.progress;
    case "elites":
      return event.type === "kill" && event.elite ? op.progress + 1 : op.progress;
    case "ace":
      return event.type === "kill" && event.ace ? op.progress + 1 : op.progress;
    case "salvage":
      return event.type === "salvage" ? op.progress + event.amount : op.progress;
    case "chain":
      return event.type === "chain" ? Math.max(op.progress, event.count) : op.progress;
    case "extract":
      return event.type === "outcome" && event.extracted ? op.progress + 1 : op.progress;
    case "survive":
      return event.type === "outcome" ? Math.max(op.progress, Math.floor(event.simTime)) : op.progress;
    case "grade":
      return event.type === "outcome" && event.extracted && GRADE_RANK[event.grade] >= op.target
        ? op.target
        : op.progress;
  }
}

function hashDateKey(dateKey: string): number {
  let acc = 2166136261;
  for (let i = 0; i < dateKey.length; i += 1) {
    acc ^= dateKey.charCodeAt(i);
    acc = Math.imul(acc, 16777619);
  }
  return hashSeed(acc >>> 0, 90210);
}

function gradeFromRank(rank: number): LetterGrade {
  if (rank >= 5) return "S";
  if (rank >= 4) return "A";
  if (rank >= 3) return "B";
  if (rank >= 2) return "C";
  return "D";
}
